import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  ArrowPathIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClockIcon,
  CloudArrowUpIcon,
  CloudArrowDownIcon
} from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { useOffline } from '@/hooks/useOffline';
import { syncService } from '@/services/syncService';
import { SyncResult } from '@/services/offlineStorage';

interface SyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSyncComplete?: (result: SyncResult) => void;
  autoTrigger?: boolean;
}

export const SyncModal: React.FC<SyncModalProps> = ({ 
  isOpen, 
  onClose, 
  onSyncComplete,
  autoTrigger = false 
}) => {
  const { isOnline, offlineData } = useOffline();
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [syncOptions, setSyncOptions] = useState({
    forceSync: false,
    skipConfirmation: false,
    syncType: 'incremental' as 'full' | 'incremental'
  });

  // Gestion des événements de synchronisation
  useEffect(() => {
    const handleSyncStart = () => {
      setSyncStatus('syncing');
      setSyncProgress(0);
      setCurrentStep('Initialisation...');
      setErrorMessage('');
    };

    const handleSyncProgress = (event: CustomEvent) => {
      const { progress, step } = event.detail;
      setSyncProgress(progress);
      setCurrentStep(step);
    };

    const handleSyncSuccess = (event: CustomEvent) => {
      const result = event.detail;
      setSyncResult(result);
      setSyncStatus('success');
      setSyncProgress(100);
      setCurrentStep('Synchronisation terminée');
      
      if (onSyncComplete) {
        onSyncComplete(result);
      }
    };

    const handleSyncError = (event: CustomEvent) => {
      const error = event.detail;
      setSyncStatus('error');
      setErrorMessage(error.message || 'Erreur inconnue');
      setCurrentStep('Erreur de synchronisation');
    };

    const handleSyncComplete = () => {
      setSyncStatus('idle');
      setSyncProgress(0);
      setCurrentStep('');
    };

    window.addEventListener('sync-start', handleSyncStart as EventListener);
    window.addEventListener('sync-progress', handleSyncProgress as EventListener);
    window.addEventListener('sync-success', handleSyncSuccess as EventListener);
    window.addEventListener('sync-error', handleSyncError as EventListener);
    window.addEventListener('sync-complete', handleSyncComplete as EventListener);

    return () => {
      window.removeEventListener('sync-start', handleSyncStart as EventListener);
      window.removeEventListener('sync-progress', handleSyncProgress as EventListener);
      window.removeEventListener('sync-success', handleSyncSuccess as EventListener);
      window.removeEventListener('sync-error', handleSyncError as EventListener);
      window.removeEventListener('sync-complete', handleSyncComplete as EventListener);
    };
  }, [onSyncComplete]);

  // Auto-trigger si demandé
  useEffect(() => {
    if (autoTrigger && isOpen && isOnline && offlineData.pendingChanges > 0) {
      setShowConfirmation(true);
    }
  }, [autoTrigger, isOpen, isOnline, offlineData.pendingChanges]);

  const handleSync = async () => {
    if (!isOnline) {
      setErrorMessage('Pas de connexion internet disponible');
      setSyncStatus('error');
      return;
    }

    try {
      setShowConfirmation(false);
      
      if (syncOptions.syncType === 'full') {
        await syncService.forceFullSync();
      } else {
        await syncService.syncData(syncOptions);
      }
    } catch (error) {
      console.error('Erreur synchronisation:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Erreur inconnue');
      setSyncStatus('error');
    }
  };

  const handleClose = () => {
    if (syncStatus === 'syncing') {
      // Demander confirmation si la sync est en cours
      if (window.confirm('La synchronisation est en cours. Voulez-vous vraiment fermer ?')) {
        setSyncStatus('idle');
        setSyncResult(null);
        setErrorMessage('');
        onClose();
      }
    } else {
      setSyncStatus('idle');
      setSyncResult(null);
      setErrorMessage('');
      onClose();
    }
  };

  const getStatusIcon = () => {
    switch (syncStatus) {
      case 'syncing':
        return <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircleIcon className="h-8 w-8 text-green-500" />;
      case 'error':
        return <XCircleIcon className="h-8 w-8 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-8 w-8 text-gray-500" />;
    }
  };

  const getStatusColor = () => {
    switch (syncStatus) {
      case 'syncing': return 'text-blue-600';
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Synchronisation des données
                </Dialog.Title>

                {/* Contenu principal */}
                <div className="space-y-4">
                  {/* Statut de la synchronisation */}
                  <div className="flex items-center space-x-3">
                    {getStatusIcon()}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${getStatusColor()}`}>
                        {syncStatus === 'idle' && 'Prêt à synchroniser'}
                        {syncStatus === 'syncing' && 'Synchronisation en cours...'}
                        {syncStatus === 'success' && 'Synchronisation réussie'}
                        {syncStatus === 'error' && 'Erreur de synchronisation'}
                      </p>
                      {currentStep && (
                        <p className="text-xs text-gray-500 mt-1">{currentStep}</p>
                      )}
                    </div>
                  </div>

                  {/* Barre de progression */}
                  {syncStatus === 'syncing' && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${syncProgress}%` }}
                      />
                    </div>
                  )}

                  {/* Informations de synchronisation */}
                  {showConfirmation && syncStatus === 'idle' && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-700">
                          <p className="font-medium mb-2">Données à synchroniser :</p>
                          <ul className="space-y-1">
                            <li className="flex items-center space-x-2">
                              <CloudArrowUpIcon className="h-4 w-4" />
                              <span>{offlineData.pendingChanges} changements locaux</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <CloudArrowDownIcon className="h-4 w-4" />
                              <span>Données de référence</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <ClockIcon className="h-4 w-4" />
                              <span>Dernière sync: {offlineData.lastSync ? offlineData.lastSync.toLocaleString() : 'Jamais'}</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Options de synchronisation */}
                  {showConfirmation && syncStatus === 'idle' && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="forceSync"
                          checked={syncOptions.forceSync}
                          onChange={(e) => setSyncOptions(prev => ({ ...prev, forceSync: e.target.checked }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="forceSync" className="text-sm text-gray-700">
                          Synchronisation complète (plus lente)
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Résultat de la synchronisation */}
                  {syncResult && syncStatus === 'success' && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-green-700">
                          <p className="font-medium mb-2">Synchronisation réussie !</p>
                          <div className="space-y-1">
                            <p>• {syncResult.uploaded} éléments uploadés</p>
                            <p>• {syncResult.downloaded} éléments téléchargés</p>
                            {syncResult.conflicts > 0 && (
                              <p>• {syncResult.conflicts} conflits résolus</p>
                            )}
                            <p>• Durée: {(syncResult.duration / 1000).toFixed(1)}s</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Message d'erreur */}
                  {errorMessage && syncStatus === 'error' && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-700">
                          <p className="font-medium mb-1">Erreur de synchronisation</p>
                          <p>{errorMessage}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Avertissement de connexion */}
                  {!isOnline && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-yellow-700">
                          <p className="font-medium">Pas de connexion internet</p>
                          <p>La synchronisation nécessite une connexion internet active.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div className="flex justify-end space-x-3 mt-6">
                  {syncStatus === 'idle' && (
                    <>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleSync}
                        disabled={!isOnline}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Synchroniser
                      </button>
                    </>
                  )}

                  {syncStatus === 'syncing' && (
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Fermer
                    </button>
                  )}

                  {(syncStatus === 'success' || syncStatus === 'error') && (
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Fermer
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Hook pour gérer le modal de synchronisation
export const useSyncModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [autoTrigger, setAutoTrigger] = useState(false);

  const openSyncModal = (auto = false) => {
    setAutoTrigger(auto);
    setIsOpen(true);
  };

  const closeSyncModal = () => {
    setIsOpen(false);
    setAutoTrigger(false);
  };

  const handleSyncComplete = (result: SyncResult) => {
    console.log('Synchronisation terminée:', result);
    // Optionnel: fermer automatiquement après un délai
    setTimeout(() => {
      closeSyncModal();
    }, 3000);
  };

  return {
    isOpen,
    autoTrigger,
    openSyncModal,
    closeSyncModal,
    handleSyncComplete
  };
};
